import routes from 'constants/routes'
import useTranslation from 'next-translate/useTranslation'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { useCallback, useEffect, useState } from 'react'
import Skeleton from 'react-loading-skeleton'
import { titleCase } from 'utils/strings'
import { getArtistUrl } from 'utils/urls'

type Breadcrumb = {
  name: string
  url?: string
  separator?: string
}

interface BreadcrumbsProps {
  separator?: React.ReactNode | string
  className?: string
  url?: string
  isArtworkPath?: boolean
  isAuctionPath?: boolean
  /**
   * To be used if it doesn't exist in the path.
   */
  artistId?: number
}

export const Breadcrumbs = ({
  className,
  separator = '/',
  url,
  isArtworkPath = false,
  isAuctionPath = false,
  artistId,
}: BreadcrumbsProps) => {
  const { t } = useTranslation()

  const urlToBreadcrumbs = useCallback(
    (url: string) => {
      const urlStrings = decodeURIComponent(url).split('/')
      const newBreadcrumbs: Breadcrumb[] = [
        {
          name: t('common:home'),
          url: '/',
        },
      ]

      if (isArtworkPath) {
        const artworkName = urlStrings[urlStrings.length - 2]

        const cleanUrlStrings = urlStrings.slice(0, -1)
        cleanUrlStrings.forEach((urlString, index) => {
          if (index === cleanUrlStrings.length - 1) {
            newBreadcrumbs.push({
              name: titleCase(urlString.replaceAll('-', ' ')),
            })
            return
          }

          const id = Number(cleanUrlStrings[index + 1])
          if (!isNaN(id) && String(id) !== artworkName) {
            newBreadcrumbs.push({
              name: titleCase(urlString.replaceAll('-', ' ')),
              url: getArtistUrl(urlString, id),
              separator: index !== cleanUrlStrings.length - 3 ? 'x' : undefined,
            })
            return
          }

          if (isNaN(Number(urlString))) {
            newBreadcrumbs.push({
              name: titleCase(urlString.replaceAll('-', ' ')),
              url: cleanUrlStrings.slice(0, index + 1).join('/'),
            })
          }
        })

        return newBreadcrumbs
      }

      if (isAuctionPath) {
        const artistName = urlStrings.at(-3)
        const auctionName = urlStrings.at(-2)

        if (artistName) {
          newBreadcrumbs.push({
            name: titleCase(artistName.replace('-', ' ')),
            url: artistId ? getArtistUrl(artistName, artistId) : routes.artists,
          })
        }

        if (auctionName) {
          newBreadcrumbs.push({
            name: titleCase(auctionName.replaceAll('-', ' ')),
          })
        }

        return newBreadcrumbs
      }

      urlStrings.slice(0, -2).forEach((urlString, index) => {
        if (!isNaN(Number(urlString))) return

        newBreadcrumbs.push({
          name: titleCase(urlString.replaceAll('-', ' ')),
          url: urlStrings.slice(0, index + 1).join('/'),
        })
      })

      // Check if the last part of the url is a number, it's an id
      if (!isNaN(Number(urlStrings[urlStrings.length - 1]))) {
        newBreadcrumbs.push({
          name: titleCase(
            urlStrings[urlStrings.length - 2].replaceAll('-', ' ')
          ),
        })

        return newBreadcrumbs
      }

      if (urlStrings.length > 2) {
        newBreadcrumbs.push({
          name: titleCase(
            urlStrings[urlStrings.length - 2].replaceAll('-', ' ')
          ),
          url: urlStrings.slice(0, urlStrings.length - 1).join('/'),
        })
      }

      newBreadcrumbs.push({
        name: titleCase(urlStrings[urlStrings.length - 1].replaceAll('-', ' ')),
      })

      return newBreadcrumbs
    },
    [artistId, isArtworkPath, isAuctionPath, t]
  )

  const router = useRouter()
  const [breadcrumbs, setBreadcrumbs] = useState<Breadcrumb[]>(() => {
    if (url) return urlToBreadcrumbs(url)

    return []
  })

  useEffect(() => {
    if (!router.isReady) return
    const url = router.asPath.includes('?')
      ? router.asPath.split('?')[0]
      : router.asPath
    const newBreadcrumbs = urlToBreadcrumbs(url)
    setBreadcrumbs(newBreadcrumbs)
  }, [router.isReady, router.asPath, urlToBreadcrumbs])

  if (!breadcrumbs?.length)
    return (
      <Skeleton
        className={`my-4 max-w-[550px] sm:my-6 ${className}`}
        width='100%'
        height={22}
        borderRadius={8}
      />
    )

  return (
    <nav
      aria-label='breadcrumbs'
      className={`my-4 flex min-h-[22px] items-center sm:my-6 ${className}`}
    >
      {breadcrumbs?.length ? (
        <ol className='-mx-1 -my-1 w-full truncate text-sm'>
          {breadcrumbs.map((breadcrumb, index) => {
            return (
              <li className='mx-1 my-1 inline space-x-2' key={index}>
                {breadcrumb.url && (
                  <Link
                    href={breadcrumb.url}
                    className='capitalize text-secondary-default'
                  >
                    {breadcrumb.name}
                  </Link>
                )}

                {!breadcrumb.url && (
                  <span className='inline capitalize text-cool-800'>
                    {breadcrumb.name}
                  </span>
                )}

                {index !== breadcrumbs.length - 1 && (
                  <>
                    {typeof separator === 'string' && (
                      <span className='text-sm text-cool-400'>
                        {breadcrumb.separator
                          ? breadcrumb.separator
                          : separator}
                      </span>
                    )}
                    {typeof separator !== 'string' && <>{separator}</>}
                  </>
                )}
              </li>
            )
          })}
        </ol>
      ) : (
        <Skeleton
          // className={`my-4 max-w-[550px] sm:my-6 ${className}`}
          width='100%'
          height={22}
          borderRadius={8}
        />
      )}
    </nav>
  )
}
